import { TEMPLATE_CATEGORIES, type TemplateCategory } from './types';

export const DOCUMENTATION_SETTINGS_PATH = '/dashboard/settings/documentation';

export type TemplateLibraryTab = TemplateCategory | 'settings';

const TAB_QUERY_PARAM = 'tab';
const ROWS_PER_PAGE_SESSION_KEY = 'unify.template-library.rows-per-page';
const DEFAULT_ROWS_PER_PAGE = '10';
const ALLOWED_ROWS_PER_PAGE = new Set(['10', '25', '50', '100']);

/** Old Template Library URL slug; still opens the Administrative tab. */
const LEGACY_TAB_QUERY: Record<string, TemplateCategory> = {
  'client-operations': 'administrative',
};

export function normalizeTemplateLibraryTab(value: string | null | undefined): TemplateLibraryTab | null {
  if (!value) return null;
  const aliased = LEGACY_TAB_QUERY[value];
  if (aliased) return aliased;
  if ((TEMPLATE_CATEGORIES as readonly string[]).includes(value) || value === 'settings') {
    return value as TemplateLibraryTab;
  }
  return null;
}

export function isTemplateLibraryTab(value: string | null | undefined): value is TemplateLibraryTab {
  if (!value) return false;
  return (TEMPLATE_CATEGORIES as readonly string[]).includes(value) || value === 'settings';
}

export function readTemplateLibraryTabFromSearch(search: string): TemplateLibraryTab | null {
  const tab = new URLSearchParams(search).get(TAB_QUERY_PARAM);
  return normalizeTemplateLibraryTab(tab);
}

export function templateLibraryListPath(tab?: TemplateLibraryTab | null): string {
  const canonical = tab ? normalizeTemplateLibraryTab(tab) ?? tab : tab;
  if (!canonical || canonical === 'appointments') {
    return DOCUMENTATION_SETTINGS_PATH;
  }
  return `${DOCUMENTATION_SETTINGS_PATH}?${TAB_QUERY_PARAM}=${encodeURIComponent(canonical)}`;
}

export function resolveTemplateLibraryReturnTab(params: {
  returnTabFromState?: unknown;
  categoryFromQuery?: string | null;
  templateCategory?: TemplateCategory | null;
  fallback?: TemplateCategory;
}): TemplateLibraryTab {
  const fromState = normalizeTemplateLibraryTab(params.returnTabFromState as string | null);
  if (fromState) return fromState;
  const fromQuery = normalizeTemplateLibraryTab(params.categoryFromQuery);
  if (fromQuery) return fromQuery;
  if (params.templateCategory) {
    return normalizeTemplateLibraryTab(params.templateCategory) ?? params.templateCategory;
  }
  return params.fallback ?? 'appointments';
}

export function readTemplateLibraryRowsPerPage(): string {
  try {
    const stored = sessionStorage.getItem(ROWS_PER_PAGE_SESSION_KEY);
    if (stored && ALLOWED_ROWS_PER_PAGE.has(stored)) {
      return stored;
    }
  } catch {
    // sessionStorage unavailable
  }
  return DEFAULT_ROWS_PER_PAGE;
}

export function writeTemplateLibraryRowsPerPage(value: string): void {
  if (!ALLOWED_ROWS_PER_PAGE.has(value)) return;
  try {
    sessionStorage.setItem(ROWS_PER_PAGE_SESSION_KEY, value);
  } catch {
    // sessionStorage unavailable
  }
}
